import { useAuth0 } from '@auth0/auth0-react';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { history } from '~/constants';
import constVars from '~/constants/constVars';
import { AuthProvider } from '~/enums';
import { accessToken } from '~/store/apis/utils/accessToken';
import { AuthContext, IAuthContext } from './AuthContext';

export const Auth0AuthProvider = ({
  children,
  hasIsAuthenticatedCookie,
}: {
  children: ReactNode;
  hasIsAuthenticatedCookie: boolean;
}) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: auth0IsLoading,
    logout: auth0Logout,
    loginWithRedirect,
  } = useAuth0();

  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
  });

  const signInWithRedirect = useCallback(
    (options: Parameters<NonNullable<IAuthContext['signInWithRedirect']>>[0]) =>
      loginWithRedirect({
        authorizationParams: {
          connection: options.connection,
          language: options.language,
          market: options.market,
          ...(options.redirectUri && {
            redirect_uri: options.redirectUri,
          }),
        },
        appState: { returnTo: options.returnTo },
      }),
    [loginWithRedirect]
  );

  const logout = useCallback(() => {
    return auth0Logout({
      logoutParams: {
        returnTo: `${globalThis.location.origin}/${constVars.ROUTE_LOGIN}`,
      },
    });
  }, []);

  const auth = useMemo<IAuthContext>(
    () => ({
      provider: AuthProvider.AUTH0,
      ...authState,
      logout,
      signInWithRedirect,
    }),
    [authState, logout, signInWithRedirect]
  );

  const fetchAuth0AccessToken = async (): Promise<string> => {
    return getAccessTokenSilently();
  };

  useEffect(() => {
    const handleAuthenticationStatus = async () => {
      const url = new URL(globalThis.location.href);
      const errorParam = url.searchParams.get('error');
      const hasLoginError = errorParam === 'login_required';
      if (hasLoginError) {
        url.searchParams.delete('error');
        url.searchParams.delete('error_description');
        url.searchParams.delete('state');
        history.replace({
          search: url.search,
        });
      }

      if (!isAuthenticated && hasIsAuthenticatedCookie && !hasLoginError) {
        sessionStorage.setItem(
          'returnTo',
          `${globalThis.location.pathname}${globalThis.location.search}`
        );

        return await loginWithRedirect({
          authorizationParams: {
            prompt: 'none',
          },
          appState: {
            returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
          },
        });
      } else if (isAuthenticated) {
        accessToken.set(fetchAuth0AccessToken);
      }

      setAuthState({ isAuthenticated, isLoading: auth0IsLoading });
    };

    if (!auth0IsLoading) {
      handleAuthenticationStatus();
    }
  }, [isAuthenticated, auth0IsLoading]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
